import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../auth/AuthContext.tsx'

export function LoginPage() {
  const { session, login } = useAuth()
  const navigate = useNavigate()
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [lockedUntil, setLockedUntil] = useState(0)
  const [now, setNow] = useState(Date.now())
  const remainingSeconds = Math.max(0, Math.ceil((lockedUntil - now) / 1000))

  useEffect(() => {
    if (remainingSeconds <= 0) return
    const interval = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(interval)
  }, [remainingSeconds])

  async function submit(fullPin: string) {
    setBusy(true)
    setError('')
    const result = await login(fullPin)
    setBusy(false)
    setPin('')
    if (result.status === 'ok') {
      navigate({ to: '/' })
    } else if (result.status === 'pending') {
      navigate({ to: '/pending-approval' })
    } else if (result.status === 'locked') {
      setNow(Date.now())
      setLockedUntil(result.lockedUntil)
    } else {
      setError(result.message)
    }
  }

  function press(digit: string) {
    if (busy || remainingSeconds > 0) return
    const next = (pin + digit).slice(0, 4)
    setPin(next)
    if (next.length === 4) void submit(next)
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (/^\d$/.test(event.key)) {
        event.preventDefault()
        press(event.key)
      } else if (event.key === 'Backspace' && !busy && remainingSeconds === 0) {
        event.preventDefault()
        setPin((value) => value.slice(0, -1))
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })

  if (session) return <Navigate to="/" />

  return (
    <div className="app-layout">
      <div className="pinpad-screen">
        <div className="center">
          <h1 style={{ fontSize: 30, margin: 0, letterSpacing: '-0.03em' }}>Sync</h1>
          <p className="muted">Enter your PIN</p>
        </div>

        <div className="pin-dots" aria-label={`${pin.length} of 4 PIN digits entered`}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`pin-dot ${i < pin.length ? 'pin-dot--filled' : ''}`} />
          ))}
        </div>

        <div className="pinpad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
            <button key={digit} disabled={busy || remainingSeconds > 0} onClick={() => press(digit)}>
              {digit}
            </button>
          ))}
          <button className="blank" tabIndex={-1} aria-hidden="true" />
          <button disabled={busy || remainingSeconds > 0} onClick={() => press('0')}>0</button>
          <button
            disabled={busy || remainingSeconds > 0}
            onClick={() => setPin((value) => value.slice(0, -1))}
            aria-label="Delete last digit"
          >
            ⌫
          </button>
        </div>

        {remainingSeconds > 0 ? (
          <p className="error">Too many attempts. Try again in {remainingSeconds}s.</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : null}

        <Link to="/forgot-pin" className="text-link">Forgot your PIN?</Link>

        {import.meta.env.DEV ? (
          <p className="muted center" style={{ fontSize: 13, maxWidth: 260 }}>
            Members: 1111 (admin) · 2222 · 3333 &nbsp;·&nbsp; Kiosk: 1442
          </p>
        ) : null}
      </div>
    </div>
  )
}
